// src/providers/AuthProvider.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode"; // You'll need to install this package

const API_URL = process.env.REACT_APP_API_URL;

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if token is expired
  const isTokenExpired = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      // Check if token has exp claim and if it's expired
      return decoded.exp && decoded.exp < currentTime;
    } catch (e) {
      // If we can't decode the token, consider it expired
      return true;
    }
  }, []);

  // Logout function that can be used throughout the app
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
  }, []);

  useEffect(() => {
    // Check if user is already logged in (token exists in localStorage)
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        // Token is expired, log out and redirect
        logout();
        window.location.href = "/login?expired=true";
      } else {
        try {
          setCurrentUser(JSON.parse(user));
        } catch (e) {
          // If user data is invalid, clear localStorage
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
    }

    setLoading(false);

    // Set up periodic token validation (every minute)
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken && isTokenExpired(currentToken)) {
        logout();
        window.location.href = "/login?expired=true";
      }
    }, 60000);

    // Clean up the interval on component unmount
    return () => clearInterval(interval);
  }, [logout, isTokenExpired]);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to login");
      }

      // Save token and user data
      localStorage.setItem("token", data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: data.userId,
          username: data.username,
        })
      );

      setCurrentUser({
        id: data.userId,
        username: data.username,
      });

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const value = {
    currentUser,
    login,
    logout,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
